"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.shouldAngleLabels = exports.shortenLabels = exports.getTargetLabelLength = exports.getAngledLabelLength = exports["default"] = exports.calculateTickDivisor = exports.calculateAxisHeight = void 0;
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _defaultNumberFormatter = _interopRequireDefault(require("./defaultNumberFormatter"));
var _cartesianLayout = require("../constants/cartesianLayout");
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2["default"])(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * Shared logic for calculating the layout of charts. Chart layout is calculated
 * as a function of calculated props and the dimensions of the chart's parent
 * container. Chart layout is passed into presentational chart components as props.
 */

/**
 * Implements heuristic for calculating a target label length, based on the root
 * mean squared average label length. May not be the final label length if additional
 * shortening is required after labels have been angled.
 */
var getTargetLabelLength = exports.getTargetLabelLength = function getTargetLabelLength(labels) {
  var labelLengths = labels.map(function (label) {
    return label.length;
  });
  var sumOfSquares = labelLengths.map(function (length) {
    return length * length;
  }).reduce(function (acc, value) {
    return acc + value;
  }, 0);
  var longestLabelLength = labelLengths.reduce(function (acc, value) {
    return value > acc ? value : acc;
  }, 0);
  var rmsAverageLength = Math.ceil(Math.sqrt(sumOfSquares / labels.length));

  /** We don't want to shorten excessively at this stage if labels are already short */
  var targetLabelLength = rmsAverageLength > _cartesianLayout.MINIMUM_LABEL_LENGTH ? rmsAverageLength : longestLabelLength;
  return targetLabelLength;
};

/**
 * Helper for determining if we should angle the labels, based on the length of the
 * longest labels and the width of the chart.
 */

var shouldAngleLabels = exports.shouldAngleLabels = function shouldAngleLabels(_ref) {
  var labels = _ref.labels,
    targetLabelLength = _ref.targetLabelLength,
    width = _ref.width;
  return width / (targetLabelLength * _cartesianLayout.LABEL_CHARACTER_WIDTH) < labels.length;
};

/**
 * Helper for calculating the height of axis with angled labels.
 */

var calculateAxisHeight = exports.calculateAxisHeight = function calculateAxisHeight(_ref2) {
  var height = _ref2.height,
    targetLabelLength = _ref2.targetLabelLength;
  return targetLabelLength * _cartesianLayout.ANGLED_LABEL_CHARACTER_HEIGHT > height * _cartesianLayout.MAXIMUM_AXIS_HEIGHT_RATIO ? height * _cartesianLayout.MAXIMUM_AXIS_HEIGHT_RATIO : targetLabelLength * _cartesianLayout.ANGLED_LABEL_CHARACTER_HEIGHT;
};
var getAngledLabelLength = exports.getAngledLabelLength = function getAngledLabelLength(_ref3) {
  var height = _ref3.height,
    targetLabelLength = _ref3.targetLabelLength;
  var maxLabelLength = Math.floor(height / _cartesianLayout.ANGLED_LABEL_CHARACTER_HEIGHT);
  return targetLabelLength > maxLabelLength ? maxLabelLength : targetLabelLength;
};
var shortenLabels = exports.shortenLabels = function shortenLabels(_ref4) {
  var labels = _ref4.labels,
    targetLength = _ref4.targetLength;
  return labels.map(function (label) {
    return label.length > targetLength ? label.slice(0, targetLength - 3).concat('...') : label;
  });
};

/**
 * Helper for applying a heuristic to calculate how many angled labels can fit
 * nicely on a chart. Result is a tickDivisor which indicates every "n" labels
 * we be kept.
 */

var calculateTickDivisor = exports.calculateTickDivisor = function calculateTickDivisor(_ref5) {
  var labels = _ref5.labels,
    width = _ref5.width;
  var culledLabelCount = width / _cartesianLayout.PIXELS_PER_ANGLED_LABEL;
  var tickDivisor = Math.floor(labels.length / culledLabelCount);
  return tickDivisor > 0 ? tickDivisor : 1;
};
/**
 * Helper for calculating props required for rendering a chart with responsive axis. Returns
 * an object with 'labels',
 * 1.) Calculate target length based on RMS Average so that outlier long labels do not skew
 *   the layout of the entire chart.
 * 2.) Decide based on the chart width and the RMS Average label length whether the axis
 *   should be angled.
 * 3.) Calculate the axis height based on the "target" longest label and the chart height.
 * 4.) Calculate the final label length based on what will fit in the calculated axis height.
 * 5.) Shorten the labels to the final length with ellipses.
 * 6.) "Cull" the labels by filtering to only show labels that are divided evenly by a tickDivisor,
 *  which is calculated as a function of width and the number of data points.
 */
var calculateResponsiveXAxis = function calculateResponsiveXAxis(_ref6) {
  var height = _ref6.height,
    width = _ref6.width,
    tickValues = _ref6.tickValues;
  var targetLabelLength = getTargetLabelLength(tickValues);
  if (shouldAngleLabels({
    labels: tickValues,
    targetLabelLength: targetLabelLength,
    width: width
  })) {
    var axisHeight = calculateAxisHeight({
      height: height,
      targetLabelLength: targetLabelLength
    });
    var angledLabelLength = getAngledLabelLength({
      height: axisHeight,
      targetLabelLength: targetLabelLength
    });
    var _shortenedLabels = shortenLabels({
      labels: tickValues,
      targetLength: angledLabelLength
    });
    var tickDivisor = calculateTickDivisor({
      labels: tickValues,
      width: width
    });
    var culledLabels = _shortenedLabels.map(function (label, index) {
      return index % tickDivisor === 0 ? label : '';
    });
    return {
      axisHeight: axisHeight,
      tickFormat: culledLabels,
      tickValues: tickValues
    };
  }
  var shortenedLabels = shortenLabels({
    labels: tickValues,
    targetLength: targetLabelLength
  });
  return {
    axisHeight: _cartesianLayout.DEFAULT_PADDING.bottom,
    tickFormat: shortenedLabels,
    tickValues: tickValues
  };
};

/**
 * Helper for calculating props for rendering a cartesian chart with a responsive
 * y axis. Currently only shortens labels to be within one standard deviation of the
 * average label length. Full implementation TBD.
 */

var calculateResponsiveYAxis = function calculateResponsiveYAxis(_ref7) {
  var tickValues = _ref7.tickValues;
  var targetLabelLength = getTargetLabelLength(tickValues);
  var shortenedLabels = shortenLabels({
    labels: tickValues,
    targetLength: targetLabelLength
  });
  return {
    tickFormat: shortenedLabels
  };
};

/**
 * Calculates layout props for the two VictoryAxis components rendered in a cartesian
 * chart (LineChart, BarChat, etc.) as well as the `padding' for the VictoryChart component
 * in both charts. Allows the charts to be responsive, relative to the orientation of the
 * charts, the size of the container they are being rendered in, and the data being rendered.
 */

var cartesianLayout = function cartesianLayout(_ref8) {
  var data = _ref8.data,
    height = _ref8.height,
    isAxisInverted = _ref8.isAxisInverted,
    isCategorical = _ref8.isCategorical,
    isTimeSeries = _ref8.isTimeSeries,
    width = _ref8.width,
    xAxis = _ref8.xAxis;
  var dependentAxisLayout = {
    tickFormat: _defaultNumberFormatter["default"]
  };
  /**
   * Timeseries and numerical data don't require that each label is shown (because
   * the labels can be interpolated by the user) so there is no need to make the axis
   * responsive.
   */
  if (isTimeSeries || !isCategorical || data.length === 0) {
    return {
      padding: _cartesianLayout.DEFAULT_PADDING,
      xAxisLayout: {},
      yAxisLayout: dependentAxisLayout
      /** For non-categorical data we can let VictoryJS set "nice" tick values */
    };
  }
  var tickValues = data.map(xAxis.value).map(function (label) {
    return "".concat(label);
  });
  if (isAxisInverted) {
    return {
      xAxisLayout: dependentAxisLayout,
      yAxisLayout: calculateResponsiveYAxis({
        tickValues: tickValues
      }),
      padding: _cartesianLayout.DEFAULT_PADDING
    };
  }
  var responsiveXAxis = calculateResponsiveXAxis({
    height: height,
    tickValues: tickValues,
    width: width
  });
  return {
    xAxisLayout: responsiveXAxis,
    yAxisLayout: dependentAxisLayout,
    padding: _objectSpread(_objectSpread({}, _cartesianLayout.DEFAULT_PADDING), {}, {
      bottom: responsiveXAxis.axisHeight
    })
  };
};
var _default = exports["default"] = {
  cartesianLayout: cartesianLayout
};
//# sourceMappingURL=CalculateLayout.js.map